import React, { useState, useEffect } from 'react';
import TradingChart from './TradingChart';
import './MarketSelector.css';  

function MarketSelector({ selectedPair, setSelectedPair, marketData, variant = 'default' }) {
  const [showMarketList, setShowMarketList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [popularPairs, setPopularPairs] = useState([]);
  const [showChart, setShowChart] = useState(false);

  // Get popular pairs
  useEffect(() => {
    const popular = [
      'BTC/USDT', 'ETH/USDT', 'BNB/USDT', 'XRP/USDT', 'ADA/USDT',
      'SOL/USDT', 'DOT/USDT', 'DOGE/USDT', 'EOS/USDT', 'LTC/USDT'
    ].filter(pair => marketData[pair]);
    
    setPopularPairs(popular);
  }, [marketData]);

  // Filter pairs based on search
  const filteredPairs = Object.keys(marketData)
    .filter(pair => 
      pair.toLowerCase().includes(searchTerm.toLowerCase()) ||
      marketData[pair]?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .slice(0, 20);

  const handlePairSelect = (pair) => {
    setSelectedPair(pair);
    setShowMarketList(false);
    setSearchTerm('');
  };

  if (variant === 'minimal') {
    return (
      <div className="market-selector-minimal">
        <button 
          className="minimal-selector-btn"
          onClick={() => setShowMarketList(!showMarketList)}
        >
          {selectedPair || 'Select Pair'} ▼
        </button>
        
        {showMarketList && (
          <div className="market-dropdown-minimal">
            <input
              type="text"
              placeholder="Search markets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="market-search-minimal"
              autoFocus
            />
            <div className="market-list-minimal">
              {filteredPairs.map(pair => (
                <button
                  key={pair}
                  className={`market-item-minimal ${selectedPair === pair ? 'active' : ''}`}
                  onClick={() => handlePairSelect(pair)}
                >
                  {pair}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (variant === 'mobile-hamburger') {
    return (
      <div className="market-selector-mobile-simple">
        <button 
          className="hamburger-btn-simple"
          onClick={() => setShowMarketList(true)}
        >
          <span className="hamburger-icon">☰</span>
          <span className="mobile-selected-pair">{selectedPair || 'Select Pair'}</span>
        </button>
        
        {showMarketList && (
          <div className="mobile-market-overlay" onClick={() => setShowMarketList(false)}>
            <div className="mobile-market-sidebar" onClick={e => e.stopPropagation()}>
              <div className="sidebar-header">
                <h3>Select Market</h3>
                <button className="close-sidebar-btn" onClick={() => setShowMarketList(false)}>✕</button>
              </div>
              
              <div className="sidebar-search">
                <input
                  type="text"
                  placeholder="Search markets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="market-search"
                  autoFocus
                />
              </div>

              <div className="sidebar-content">
                <div className="popular-section">
                  <h5>Popular Pairs</h5>
                  <div className="popular-pairs">
                    {popularPairs.map(pair => (
                      <button
                        key={pair}
                        className="popular-pair-btn"
                        onClick={() => handlePairSelect(pair)}
                      >
                        {pair}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="all-markets-section">
                  <h5>All Markets ({filteredPairs.length})</h5>
                  <div className="market-list">
                    {filteredPairs.map(pair => {
                      const pairData = marketData[pair];
                      return (
                        <button
                          key={pair}
                          className={`market-item ${selectedPair === pair ? 'active' : ''}`}
                          onClick={() => handlePairSelect(pair)}
                        >
                          <div className="pair-info">
                            <span className="pair-symbol">{pair}</span>
                            {pairData && (
                              <span className="pair-price">
                                ${pairData.current_price?.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 6
                                })}
                              </span>
                            )}
                          </div>
                          {pairData && (
                            <span className={`pair-change ${
                              pairData.price_change_percentage_24h >= 0 ? 'positive' : 'negative'
                            }`}>
                              {pairData.price_change_percentage_24h >= 0 ? '+' : ''}
                              {pairData.price_change_percentage_24h?.toFixed(2)}%
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (variant === 'mobile') {
    return (
      <div className="market-selector-mobile">
        <div className="mobile-header-row">
          <button 
            className="hamburger-btn"
            onClick={() => setShowMarketList(true)}
          >
            <span className="hamburger-icon">☰</span>
            <span className="mobile-selected-pair">{selectedPair || 'Select Pair'}</span>
          </button>

          <button 
            className={`chart-toggle-btn-mobile ${showChart ? 'active' : ''}`}
            onClick={() => setShowChart(!showChart)}
            title="Toggle Chart"
          >
            📈
          </button>
        </div>
        
        {showMarketList && (
          <div className="mobile-market-overlay" onClick={() => setShowMarketList(false)}>
            <div className="mobile-market-sidebar" onClick={e => e.stopPropagation()}>
              <div className="sidebar-header">
                <h3>Select Market</h3>
                <button className="close-sidebar-btn" onClick={() => setShowMarketList(false)}>✕</button>
              </div>
              
              <div className="sidebar-search">
                <input
                  type="text"
                  placeholder="Search markets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="market-search"
                  autoFocus
                />
              </div>

              <div className="sidebar-content">
                <div className="popular-section">
                  <h5>Popular Pairs</h5>
                  <div className="popular-pairs">
                    {popularPairs.map(pair => (
                      <button
                        key={pair}
                        className="popular-pair-btn"
                        onClick={() => handlePairSelect(pair)}
                      >
                        {pair}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="all-markets-section">
                  <h5>All Markets ({filteredPairs.length})</h5>
                  <div className="market-list">
                    {filteredPairs.map(pair => {
                      const pairData = marketData[pair];
                      return (
                        <button
                          key={pair}
                          className={`market-item ${selectedPair === pair ? 'active' : ''}`}
                          onClick={() => handlePairSelect(pair)}
                        >
                          <div className="pair-info">
                            <span className="pair-symbol">{pair}</span>
                            {pairData && (
                              <span className="pair-price">
                                ${pairData.current_price?.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 6
                                })}
                              </span>
                            )}
                          </div>
                          {pairData && (
                            <span className={`pair-change ${
                              pairData.price_change_percentage_24h >= 0 ? 'positive' : 'negative'
                            }`}>
                              {pairData.price_change_percentage_24h >= 0 ? '+' : ''}
                              {pairData.price_change_percentage_24h?.toFixed(2)}%
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showChart && selectedPair && (
          <div className="market-chart-overlay">
            <div className="market-chart-modal">
              <div className="chart-modal-header">
                <h3>{selectedPair} Price Chart</h3>
                <button className="close-chart-btn" onClick={() => setShowChart(false)}>✕</button>
              </div>
              <div className="chart-modal-content">
                <TradingChart 
                  pair={selectedPair} 
                  marketData={marketData} 
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="market-selector-wrapper">
      <div className="market-selector">
        <button 
          className="selector-btn"
          onClick={() => setShowMarketList(!showMarketList)}
        >
          <span className="selected-pair">{selectedPair || 'Select Trading Pair'}</span>
          <span className="dropdown-arrow">▼</span>
        </button>
        
        <button 
          className={`chart-toggle-btn ${showChart ? 'active' : ''}`}
          onClick={() => setShowChart(!showChart)}
          title="Toggle Chart"
        >
          📈
        </button>

        {showMarketList && (
          <div className="market-dropdown">
            <div className="dropdown-header">
              <h4>Select Market</h4>
              <input
                type="text"
                placeholder="Search 1000+ markets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="market-search"
                autoFocus
              />
            </div>

            <div className="popular-section">
              <h5>Popular Pairs</h5>
              <div className="popular-pairs">
                {popularPairs.map(pair => (
                  <button
                    key={pair}
                    className="popular-pair-btn"
                    onClick={() => handlePairSelect(pair)}
                  >
                    {pair}
                  </button>
                ))}
              </div>
            </div>

            <div className="all-markets-section">
              <h5>All Markets ({filteredPairs.length})</h5>
              <div className="market-list">
                {filteredPairs.map(pair => {
                  const pairData = marketData[pair];
                  return (
                    <button
                      key={pair}
                      className={`market-item ${selectedPair === pair ? 'active' : ''}`}
                      onClick={() => handlePairSelect(pair)}
                    >
                      <div className="pair-info">
                        <span className="pair-symbol">{pair}</span>
                        {pairData && (
                          <span className="pair-price">
                            ${pairData.current_price?.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 6
                            })}
                          </span>
                        )}
                      </div>
                      {pairData && (
                        <span className={`pair-change ${
                          pairData.price_change_percentage_24h >= 0 ? 'positive' : 'negative'
                        }`}>
                          {pairData.price_change_percentage_24h >= 0 ? '+' : ''}
                          {pairData.price_change_percentage_24h?.toFixed(2)}%
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {showChart && selectedPair && (
        <div className="market-chart-overlay">
          <div className="market-chart-modal">
            <div className="chart-modal-header">
              <h3>{selectedPair} Price Chart</h3>
              <button className="close-chart-btn" onClick={() => setShowChart(false)}>✕</button>
            </div>
            <div className="chart-modal-content">
              <TradingChart 
                pair={selectedPair} 
                marketData={marketData} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketSelector;